/**
 * Bot AI system. Bots are just entities that produce Intents — exactly like the
 * player and (later) networked players — so they flow through the same
 * movement/action/eating systems. That uniformity is what lets bots be blended
 * with or replaced by real players later.
 *
 * Behaviour: wander, hunt smaller targets, flee bigger threats. Decisions are
 * only re-evaluated every `reactionTime` (with jitter) so bots feel like they
 * have a reaction delay rather than perfect, instant reflexes. Between
 * decisions they keep steering toward/away from their chosen target's live
 * position, so motion stays smooth and responsive.
 */
import type { GameConfig } from '../../config/config';
import { Rng } from '../math/rng';
import { distance, normalize, sub } from '../math/vec2';
import { SpatialHash } from '../spatialHash';
import type { Entity, EntityId } from '../types';

function perceptionRange(size: number, cfg: GameConfig): number {
  return cfg.bots.perceptionBase + size * cfg.bots.perceptionPerSize;
}

const scratch: Entity[] = [];

function think(
  bot: Entity,
  spatial: SpatialHash,
  cfg: GameConfig,
  time: number,
  rng: Rng,
): void {
  const ai = bot.ai!;
  const range = perceptionRange(bot.size, cfg);
  const neighbors = spatial.queryRadius(bot.pos.x, bot.pos.y, range, scratch);

  let threat: Entity | null = null;
  let threatDist = Infinity;
  let prey: Entity | null = null;
  let preyDist = Infinity;

  for (const n of neighbors) {
    if (n === bot || !n.alive) continue;
    const d = distance(bot.pos, n.pos);
    if (d > range) continue;
    if (n.size >= bot.size * cfg.bots.fleeSizeRatio) {
      if (d < threatDist) {
        threatDist = d;
        threat = n;
      }
    } else if (bot.size >= n.size * cfg.eat.sizeRatio) {
      if (d < preyDist) {
        preyDist = d;
        prey = n;
      }
    }
  }

  if (threat) {
    ai.mode = 'flee';
    ai.targetId = threat.id;
  } else if (prey) {
    ai.mode = 'hunt';
    ai.targetId = prey.id;
  } else {
    ai.mode = 'wander';
    ai.targetId = null;
  }

  ai.nextThinkTime = time + cfg.bots.reactionTime + rng.range(0, cfg.bots.reactionJitter);
}

export function updateBots(
  entities: Entity[],
  spatial: SpatialHash,
  byId: Map<EntityId, Entity>,
  cfg: GameConfig,
  time: number,
  dt: number,
  rng: Rng,
): void {
  for (const bot of entities) {
    if (!bot.alive || bot.kind !== 'bot' || !bot.ai) continue;
    const ai = bot.ai;

    if (time >= ai.nextThinkTime) think(bot, spatial, cfg, time, rng);

    const target = ai.targetId != null ? byId.get(ai.targetId) : undefined;

    if (ai.mode === 'flee' && target && target.alive) {
      ai.heading = normalize(sub(bot.pos, target.pos));
      bot.intent.pulses.shrink = (bot.intent.pulses.shrink ?? 0) + cfg.bots.shrinkClicksPerSec * dt;
    } else if (ai.mode === 'hunt' && target && target.alive) {
      ai.heading = normalize(sub(target.pos, bot.pos));
      bot.intent.pulses.grow = (bot.intent.pulses.grow ?? 0) + cfg.bots.growClicksPerSec * dt;
    } else {
      // Wander: pick a fresh random heading periodically, drift, grow slowly.
      if (time >= ai.nextWanderTime) {
        ai.heading = rng.direction();
        ai.nextWanderTime = time + cfg.bots.wanderInterval;
        ai.mode = 'wander';
      }
      bot.intent.pulses.grow =
        (bot.intent.pulses.grow ?? 0) + cfg.bots.growClicksPerSec * 0.4 * dt;
    }

    bot.intent.moveDir = ai.heading;
  }
}
